import { CSSProperties, forwardRef, ReactNode } from 'react';
import { DataEditor, DataEditorProps, DataEditorRef, SpriteMap } from '@glideapps/glide-data-grid';
import { renderToString } from 'react-dom/server';
import {
    TbCalendarBolt,
    TbCalendarEvent,
    TbCurrencyDollar,
    TbDots,
    TbGraph,
    TbInfoCircle,
    TbList,
    TbPercentage,
    TbPlus,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useGlideDataGridTheme } from '@/hooks/useGlideDataGridTheme';
import { glideCustomCellRenderers } from '@/shared/components/tables/glideCustomCells';

import '@glideapps/glide-data-grid/dist/index.css';

type CentralizedDataEditorProp =
    | 'cellActivationBehavior'
    | 'customRenderers'
    | 'experimental'
    | 'headerIcons'
    | 'height'
    | 'maxColumnAutoWidth'
    | 'minColumnWidth'
    | 'scaleToRem'
    | 'smoothScrollX'
    | 'smoothScrollY'
    | 'theme'
    | 'verticalBorder'
    | 'width';

interface FinancialGlideDataGridProps extends Omit<DataEditorProps, CentralizedDataEditorProp | 'freezeColumns'> {
    children?: ReactNode;
    dataSelectionControl?: boolean;
    freezeColumns?: number;
    height?: CSSProperties['height'];
}

const financialHeaderIcons: SpriteMap = {
    TbCalendarBolt: (props) => renderToString(<TbCalendarBolt style={{ color: props.bgColor }} />),
    TbCalendarEvent: (props) => renderToString(<TbCalendarEvent style={{ color: props.bgColor }} />),
    TbCurrencyDollar: (props) => renderToString(<TbCurrencyDollar style={{ color: props.bgColor }} />),
    TbDots: (props) => renderToString(<TbDots style={{ color: props.bgColor }} />),
    TbGraph: (props) => renderToString(<TbGraph style={{ color: props.bgColor }} />),
    TbInfoCircle: (props) => renderToString(<TbInfoCircle style={{ color: props.bgColor }} />),
    TbList: (props) => renderToString(<TbList style={{ color: props.bgColor }} />),
    TbPercentage: (props) => renderToString(<TbPercentage style={{ color: props.bgColor }} />),
    TbPlus: (props) => renderToString(<TbPlus style={{ color: props.bgColor }} />),
};

const FinancialGlideDataGrid = forwardRef<DataEditorRef, FinancialGlideDataGridProps>(
    ({ children, dataSelectionControl, freezeColumns = 2, height, ...dataEditorProps }, ref) => {
        const { token } = useAntDesignTheme();
        const { tableTheme } = useGlideDataGridTheme();

        return (
            <div
                data-selection-control={dataSelectionControl ? 'true' : undefined}
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    boxSizing: 'border-box',
                    height: height ?? '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <DataEditor
                    {...dataEditorProps}
                    ref={ref}
                    cellActivationBehavior='second-click'
                    customRenderers={glideCustomCellRenderers}
                    experimental={{ kineticScrollPerfHack: true, strict: true }}
                    freezeColumns={freezeColumns}
                    headerIcons={financialHeaderIcons}
                    height='100%'
                    maxColumnAutoWidth={200}
                    minColumnWidth={16 * 14}
                    scaleToRem={true}
                    smoothScrollX={true}
                    smoothScrollY={true}
                    theme={tableTheme}
                    verticalBorder={true}
                    width='100%'
                />
                {children}
            </div>
        );
    },
);

FinancialGlideDataGrid.displayName = 'FinancialGlideDataGrid';

export default FinancialGlideDataGrid;
